import { useLayoutHeight, useLayoutWidth } from 'app/imports/App.hooks'
import { selectSchool, selectSport } from 'app/imports/App.selectors'
import { cszUrl } from 'features/Env'
import { UISelect, UISpinner } from 'features/UI'
import { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { SingleValue } from 'react-select'
import { getQueryParams } from 'utils'
import { DefaultSeasonValue, FilterOption, gameTypes } from '../../Schedules.model'
import { useGetSchedulesQuery } from '../../state/Schedules.rtk'
import { ScheduleItem } from '../ScheduleItem/ScheduleItem'
import { SchedulesEmptyLabel } from '../SchedulesEmptyLabel/SchedulesEmptyLabel'
import { SchedulesFilterSeason } from '../SchedulesFilterSeason/SchedulesFilterSeason'
import * as Styled from './Schedules.styled'

export function Schedules() {
  const layoutWidth = useLayoutWidth()
  const layoutHeight = useLayoutHeight()

  const { state, district } = getQueryParams()
  const [eventType, setEventType] = useState(gameTypes[0])
  const [season, setSeason] = useState(DefaultSeasonValue)
  const schoolData = useSelector(selectSchool)
  const sportData = useSelector(selectSport)

  const { data, isFetching } = useGetSchedulesQuery(
    {
      stateId: state,
      districtId: district,
      schoolId: schoolData?.id,
      sportId: sportData?.id,
      isGame: eventType.id === 'Game',
      season: season.id,
    },
    {
      skip: !state || !district || !schoolData || !sportData,
      refetchOnMountOrArgChange: true,
    },
  )

  const handleEventTypeChange = useCallback((event: SingleValue<FilterOption>) => {
    if (!event) return

    const gameType = gameTypes.find(({ id }) => id === event.name)
    if (!gameType) return

    setEventType(gameType)
  }, [])

  const handleSeasonChange = useCallback((season: FilterOption) => {
    setSeason(season)
  }, [])

  const seasonShort = useMemo(() => {
    if (!season) return ''

    const [startYear, endYear] = season.id.split('-')
    const formattedStartYear = startYear.slice(-2)
    const formattedEndYear = endYear.slice(-2)
    return `${formattedStartYear}-${formattedEndYear}`
  }, [season])

  const getUrlParams = useCallback(() => {
    const params = new URLSearchParams({
      season: season.id,
      force: 'true',
    })

    if (state) {
      params.set('selectedStateId', state)
    }

    if (district) {
      params.set('selectedCountyId', district)
    }

    if (sportData) {
      params.set('selectedSportId', sportData.id)
    }

    return params
  }, [season, state, district, sportData])

  const standingsUrl = useMemo(() => {
    return `${cszUrl}/scores/standings?${getUrlParams().toString()}`
  }, [getUrlParams])

  const scoresUrl = useMemo(() => {
    return `${cszUrl}/scores?${getUrlParams().toString()}`
  }, [getUrlParams])

  const teamUrl = useMemo(() => {
    const params = new URLSearchParams({
      season: season.id,
      force: 'true',
    })

    if (state) {
      params.set('stateId', state)
    }

    if (district) {
      params.set('countyId', district)
    }

    if (sportData) {
      params.set('sportId', sportData.id)
    }

    if (schoolData) {
      params.set('schoolId', schoolData.id)
    }

    return `${cszUrl}/schedules?${params.toString()}`
  }, [season, state, district, sportData, schoolData])

  return (
    <Styled.Container $layoutHeight={layoutHeight}>
      <Styled.TitleContainer $width={layoutWidth}>
        <Styled.Title>{`${schoolData?.shortName} ${sportData?.name} ${seasonShort}`}</Styled.Title>

        <Styled.Links>
          <Styled.Link href={teamUrl} target="_blank">
            Team Page
          </Styled.Link>
          <Styled.Link href={standingsUrl} target="_blank">
            Standings
          </Styled.Link>
          <Styled.Link href={scoresUrl} target="_blank">
            League Scores
          </Styled.Link>
        </Styled.Links>
      </Styled.TitleContainer>

      <Styled.Filters>
        <Styled.EventTypeFilter>
          <UISelect<FilterOption, false>
            options={gameTypes}
            value={eventType}
            placeholder="Event type"
            onChange={handleEventTypeChange}
            isMulti={false}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id}
            isClearable={false}
          />
        </Styled.EventTypeFilter>

        <Styled.SeasonFilter>
          <SchedulesFilterSeason value={season} onChange={handleSeasonChange} />
        </Styled.SeasonFilter>
      </Styled.Filters>

      {(isFetching || !data) && (
        <div style={{ marginTop: '6px' }}>
          <UISpinner />
        </div>
      )}

      {!isFetching && data?.schedules.length === 0 && <SchedulesEmptyLabel />}

      {!isFetching && data && (
        <Styled.List>
          {data.schedules.map((schedule) => (
            <ScheduleItem schedule={schedule} key={schedule.sportId} />
          ))}
        </Styled.List>
      )}
    </Styled.Container>
  )
}
